// POKERCITY DEALER CERTIFICATION — QUESTION BANK
// Track: Professional | Category: TDA Rules & Rulings
// Batch 1 of 6 — Questions 1-40 (TDA Rules)
// Difficulty: E=Easy, M=Medium, H=Hard

export const TDA_RULES_QUESTIONS = [

  // ── EASY (8 questions) ──────────────────────────────────────────────────

  {
    id: 'tda_001', track: 'professional', category: 'tda_rules', difficulty: 'easy',
    question: 'A player verbally says "I call" and then tries to change their action. What is the ruling?',
    options: [
      'A) The player may change their action once per hand',
      'B) The verbal declaration is binding — the call stands',
      'C) The player may change if cards have not been dealt',
      'D) Only written declarations are binding'
    ],
    correct: 'B',
    explanation: 'Per TDA Rules, a clear verbal declaration of action is binding. Once a player says "call," that action stands regardless of subsequent attempts to change it.'
  },

  {
    id: 'tda_002', track: 'professional', category: 'tda_rules', difficulty: 'easy',
    question: 'What is a "string bet"?',
    options: [
      'A) A bet made with chips from multiple stacks',
      'B) Placing chips forward then returning to the stack for more without a prior verbal declaration',
      'C) A bet that matches the exact size of the previous bet',
      'D) A bet made out of turn'
    ],
    correct: 'B',
    explanation: 'A string bet occurs when a player moves chips forward, then returns to their stack for more chips without first verbally declaring the full raise amount. Only the initial forward motion counts.'
  },

  {
    id: 'tda_003', track: 'professional', category: 'tda_rules', difficulty: 'easy',
    question: 'A player\'s hand touches the muck. What is the ruling?',
    options: [
      'A) The hand is retrievable if the dealer can identify the cards',
      'B) The hand is dead — it cannot be retrieved',
      'C) The floor must be called to make the ruling',
      'D) The hand is dead only if another player objects'
    ],
    correct: 'B',
    explanation: 'Once a hand contacts the muck it is dead and cannot be retrieved, even if the cards are identifiable. This protects the integrity of the muck.'
  },

  {
    id: 'tda_004', track: 'professional', category: 'tda_rules', difficulty: 'easy',
    question: 'How many cards constitute a misdeal in most TDA-governed tournaments?',
    options: [
      'A) Any incorrect number of cards to any player',
      'B) Only if the first card is dealt face-up',
      'C) Only if two or more cards are exposed during the deal',
      'D) Only if the dealer deals to a dead position'
    ],
    correct: 'A',
    explanation: 'A misdeal is declared when any player receives an incorrect number of cards, among other conditions. The hand is redealt from the beginning.'
  },

  {
    id: 'tda_005', track: 'professional', category: 'tda_rules', difficulty: 'easy',
    question: 'A player puts out a single oversized chip without verbal declaration before the flop. What does this action mean?',
    options: [
      'A) It is a raise to the amount of the chip',
      'B) It is a call — change is returned',
      'C) The player must announce their intention before the chip is released',
      'D) It counts as a fold'
    ],
    correct: 'B',
    explanation: 'A single oversized chip placed forward without verbal declaration is a call. Change is returned. To raise with an oversized chip the player must verbally declare the raise amount before releasing the chip.'
  },

  {
    id: 'tda_006', track: 'professional', category: 'tda_rules', difficulty: 'easy',
    question: 'What is the minimum raise size in a no-limit game?',
    options: [
      'A) Double the big blind at all times',
      'B) At least equal to the previous bet or raise in that round',
      'C) Any amount the player chooses',
      'D) At least 50% of the previous bet'
    ],
    correct: 'B',
    explanation: 'The minimum raise must be at least equal to the previous bet or raise in that betting round. Example: if someone bets 200, the minimum raise is to 400 (a raise of 200).'
  },

  {
    id: 'tda_007', track: 'professional', category: 'tda_rules', difficulty: 'easy',
    question: 'A player accidentally flips over a card during the deal. What do you do?',
    options: [
      'A) Continue dealing — the exposed card stays with that player',
      'B) Immediately call the floor',
      'C) Complete the deal, then use the exposed card as the first burn card',
      'D) Declare a misdeal immediately'
    ],
    correct: 'C',
    explanation: 'If one card is exposed during the deal, the dealer completes the deal normally. The exposed card is shown to all players, placed on top of the deck as the first burn card, and a replacement card is dealt to that player.'
  },

  {
    id: 'tda_008', track: 'professional', category: 'tda_rules', difficulty: 'easy',
    question: 'When must a player show your cards at showdown?',
    options: [
      'A) Whenever the dealer asks',
      'B) Only if they are called — the last aggressor may muck uncontested',
      'C) All remaining players must show at showdown regardless',
      'D) Only the winner needs to show'
    ],
    correct: 'B',
    explanation: 'At showdown, a player who was called must show their hand. The last aggressor (bettor/raiser) who was not called may muck without showing. All-in players who are called must show.'
  },

  // ── MEDIUM (18 questions) ───────────────────────────────────────────────

  {
    id: 'tda_009', track: 'professional', category: 'tda_rules', difficulty: 'medium',
    question: 'Player A bets 500. Player B raises to 1,000. Player C wants to re-raise. What is the minimum re-raise?',
    options: [
      'A) 1,500 (adding 500 to the raise amount)',
      'B) 2,000 (doubling Player B\'s total bet)',
      'C) 1,500 total (adding the raise increment of 500)',
      'D) Any amount over 1,000'
    ],
    correct: 'A',
    explanation: 'The raise increment was 500 (from 500 to 1,000). The minimum re-raise adds that same increment: 1,000 + 500 = 1,500 total. The raise size (not the total bet) sets the minimum re-raise.'
  },

  {
    id: 'tda_010', track: 'professional', category: 'tda_rules', difficulty: 'medium',
    question: 'A player is all-in for less than a full raise. The next player wants to raise. Can they?',
    options: [
      'A) Yes — any all-in reopens the action for a full raise',
      'B) No — an all-in for less than a full raise does not reopen raising for players who already acted',
      'C) Only if the all-in is at least 50% of the full raise amount',
      'D) Yes, but only if two or more players agree'
    ],
    correct: 'B',
    explanation: 'An all-in for less than a full raise does not reopen the betting. Players who already acted (called) may not raise again. Only a full raise reopens action to all players.'
  },

  {
    id: 'tda_011', track: 'professional', category: 'tda_rules', difficulty: 'medium',
    question: 'A player acts out of turn and folds. What is the ruling?',
    options: [
      'A) The fold stands — the player is out of the hand',
      'B) The fold may be binding depending on whether the action changes before it reaches them',
      'C) The fold is always reversed — the player must act in turn',
      'D) The floor must be called and a penalty assessed'
    ],
    correct: 'B',
    explanation: 'An out-of-turn fold is binding if the action does not change before it reaches that player. If the action changes (e.g. a raise), the player may reconsider their action.'
  },

  {
    id: 'tda_012', track: 'professional', category: 'tda_rules', difficulty: 'medium',
    question: 'Two players go to showdown. Player A has the best hand but throws your cards into the muck before the pot is awarded. What happens?',
    options: [
      'A) Player A wins — the dealer saw the hand',
      'B) Player B wins — Player A\'s hand is dead',
      'C) The floor is called to reconstruct the hand from memory',
      'D) The pot is split equally'
    ],
    correct: 'B',
    explanation: 'Cards speak only when tabled (face up). A hand thrown into the muck is dead regardless of its value. Player A loses the pot. This is why dealers should encourage players to table hands before mucking at showdown.'
  },

  {
    id: 'tda_013', track: 'professional', category: 'tda_rules', difficulty: 'medium',
    question: 'What is the correct ruling when a player bets and their only remaining opponent has already posted a forced bet that covers the bet amount?',
    options: [
      'A) The opponent must call, raise, or fold as normal',
      'B) The bet is not allowed since the forced bet already covers it',
      'C) The opponent may check — the bet closes the action',
      'D) The forced bet is returned and a new round begins'
    ],
    correct: 'A',
    explanation: 'A forced bet (blind) does not constitute calling or acting. The opponent still has the full range of options: fold, call the additional amount, or raise.'
  },

  {
    id: 'tda_014', track: 'professional', category: 'tda_rules', difficulty: 'medium',
    question: 'A player splashes the pot. What do you do?',
    options: [
      'A) Continue play — pot splashing is not a violation',
      'B) Stop action, separate the bet from the pot, verify the amount, then continue',
      'C) Call the floor immediately for a ruling',
      'D) Ask the player to recount their chips but continue action'
    ],
    correct: 'B',
    explanation: 'When a player splashes the pot the dealer must stop action, separate the bet from the pot to verify the correct amount, then continue. This protects all players from disputes about bet size.'
  },

  {
    id: 'tda_015', track: 'professional', category: 'tda_rules', difficulty: 'medium',
    question: 'A player is found to have three hole cards. What is the ruling?',
    options: [
      'A) The player discards one card of their choice',
      'B) The dealer randomly removes one card — the hand continues',
      'C) The hand is a misdeal — all cards are returned and redealt',
      'D) The floor decides based on whether anyone has seen the extra card'
    ],
    correct: 'C',
    explanation: 'If a player is found to have an incorrect number of hole cards before significant action, it is a misdeal. If discovered after significant action, the floor makes a ruling — typically the hand is dead.'
  },

  {
    id: 'tda_016', track: 'professional', category: 'tda_rules', difficulty: 'medium',
    question: 'The board reads A♠ K♠ Q♠ J♠ T♠. Player A shows 9♠ 8♠. Player B shows 2♣ 2♦. Who wins?',
    options: [
      'A) Player A wins with a straight flush',
      'B) Player B wins with a pair',
      'C) The pot is split — both players play the board',
      'D) Player A wins with a higher board play'
    ],
    correct: 'C',
    explanation: 'The board is a royal flush (A K Q J T suited) — the best possible hand. Both players\' hole cards are irrelevant. The best 5-card hand is on the board, so the pot is split equally.'
  },

  {
    id: 'tda_017', track: 'professional', category: 'tda_rules', difficulty: 'medium',
    question: 'A player faces a bet and says "how much do you have behind?" then puts in chips. Is this action binding?',
    options: [
      'A) No — asking a question voids any subsequent action',
      'B) Yes — placing chips forward is a binding action regardless of prior speech',
      'C) Only if the player had already verbally declared their action',
      'D) No — the player must wait for an answer before acting'
    ],
    correct: 'B',
    explanation: 'Placing chips forward is a binding action. Asking a question does not suspend or void the subsequent physical action. The chips forward constitute a call or raise depending on amount.'
  },

  {
    id: 'tda_018', track: 'professional', category: 'tda_rules', difficulty: 'medium',
    question: 'What is the correct procedure when a player at a full table busts out and a new player takes their seat mid-hand?',
    options: [
      'A) The new player posts a big blind immediately and receives cards',
      'B) The new player waits until the next hand to receive cards',
      'C) The new player may buy in but cannot receive cards until it is the big blind position',
      'D) The new player can choose when to post and receive cards'
    ],
    correct: 'B',
    explanation: 'A new player at the table does not receive cards mid-hand. They wait for the next deal. They may be required to post a big blind or wait for the big blind to reach their position depending on tournament rules.'
  },

  {
    id: 'tda_019', track: 'professional', category: 'tda_rules', difficulty: 'medium',
    question: 'A player puts in a raise but the amount is only 40% of the required minimum raise. What is the ruling?',
    options: [
      'A) The raise stands — any amount forward is a raise',
      'B) The action is a call — insufficient for a raise, returned to call amount',
      'C) The raise is rounded up to the minimum raise amount automatically',
      'D) The player must add the remainder or fold'
    ],
    correct: 'B',
    explanation: 'If chips put forward are insufficient for a raise (less than 50% of the minimum raise in most rules), the action defaults to a call. The excess chips are returned. Some tournaments use a 50% rule — always know your specific rules.'
  },

  {
    id: 'tda_020', track: 'professional', category: 'tda_rules', difficulty: 'medium',
    question: 'A player asks "is that a raise?" when facing a bet on the river. How do you respond?',
    options: [
      'A) Confirm the action and the amount',
      'B) Remain silent — the dealer does not speak during hands',
      'C) Direct the question to the floor',
      'D) Ask the player to act without further information'
    ],
    correct: 'A',
    explanation: 'The dealer should confirm the action type (bet/raise) and the amount when a player sincerely asks. This is part of keeping the game orderly. The dealer should not volunteer unsolicited game advice but must answer factual questions about the action.'
  },

  {
    id: 'tda_021', track: 'professional', category: 'tda_rules', difficulty: 'medium',
    question: 'During a hand, a player exposes their hole cards to one other player deliberately. What is the ruling?',
    options: [
      'A) No penalty — it is the other player\'s responsibility not to look',
      'B) The hand is dead',
      'C) The exposed cards must be shown to all players at the table',
      'D) The floor assesses a penalty at their discretion'
    ],
    correct: 'C',
    explanation: 'If a player deliberately shows their cards to any active player during a hand, all active players are entitled to see the exposed cards. The dealer announces the cards to the table.'
  },

  {
    id: 'tda_022', track: 'professional', category: 'tda_rules', difficulty: 'medium',
    question: 'You burn and turns the river card but there are still players to act on the turn. What is the correct procedure?',
    options: [
      'A) Leave the river card exposed — it becomes the river',
      'B) Place the river card face down to the side, complete turn action, then turn it as the river',
      'C) Declare a misdeal and redeal from the beginning',
      'D) Shuffle the river card back into the deck'
    ],
    correct: 'B',
    explanation: 'The prematurely dealt river card is placed face down to the side (not shuffled back in). Turn action is completed normally. Then that card is turned as the river. The deck is not reshuffled.'
  },

  {
    id: 'tda_023', track: 'professional', category: 'tda_rules', difficulty: 'medium',
    question: 'A player is away from the table when the hand is dealt. What happens to your hand?',
    options: [
      'A) The hand is dealt and held — the player may return to act',
      'B) The hand is dealt and folded automatically after the action clock expires',
      'C) The hand is not dealt if the player is not present at the start of the deal',
      'D) An absent player\'s hand is folded immediately before dealing'
    ],
    correct: 'D',
    explanation: 'In tournament play, players must be present at the start of the deal to receive a hand. If absent, the hand is not dealt to them (or is immediately killed depending on the specific rule set). Blinds are still posted for absent players.'
  },

  {
    id: 'tda_024', track: 'professional', category: 'tda_rules', difficulty: 'medium',
    question: 'What does "cards speak" mean in practice for the dealer?',
    options: [
      'A) Dealers must read all hands aloud at showdown',
      'B) The best hand wins based on the actual cards shown, regardless of what players announce',
      'C) Players must verbally declare their hand before showing cards',
      'D) Dealers may not correct a player\'s misread of their own hand'
    ],
    correct: 'B',
    explanation: '"Cards speak" means the actual cards determine the winner, not what players say they have. If a player misreads their hand and the dealer sees a better holding, the dealer should announce the correct hand. The dealer protects all players\' interests.'
  },

  {
    id: 'tda_025', track: 'professional', category: 'tda_rules', difficulty: 'medium',
    question: 'A player goes all-in and is called by two players. One caller has more chips than the all-in; the other caller has fewer. How are the side pots structured?',
    options: [
      'A) One main pot — all chips go together regardless of stack sizes',
      'B) Main pot (all three contribute equally to all-in amount) plus one side pot between the two callers',
      'C) Two separate side pots, one for each caller',
      'D) The player with fewest chips determines all pot sizes'
    ],
    correct: 'B',
    explanation: 'The main pot contains the all-in amount × number of players (3). The side pot contains the additional chips between the two callers only. The all-in player cannot win the side pot.'
  },

  {
    id: 'tda_026', track: 'professional', category: 'tda_rules', difficulty: 'medium',
    question: 'A player is on a clock (shot clock). The clock expires. What happens?',
    options: [
      'A) The hand is folded automatically',
      'B) The floor is called to make a ruling',
      'C) The dealer calls clock and gives a final 10-second warning before the hand is dead',
      'D) Other players vote on whether to extend the clock'
    ],
    correct: 'A',
    explanation: 'When a shot clock expires the hand is dead — folded automatically. In events using a shot clock, the dealer (or floor) counts down and the hand is killed when time expires. The procedure varies by event rules.'
  },

  // ── HARD (14 questions) ─────────────────────────────────────────────────

  {
    id: 'tda_027', track: 'professional', category: 'tda_rules', difficulty: 'hard',
    question: 'Three players are all-in: Player A (100 chips), Player B (300 chips), Player C (700 chips). Player C has the best hand, Player A has second best. How is the pot distributed?',
    options: [
      'A) Player C wins everything',
      'B) Player C wins the main pot and side pot; Player A wins nothing',
      'C) Main pot (300 chips) to Player C; side pot 1 (400 chips) to Player C; side pot 2 (400 chips) to Player C',
      'D) Main pot (300) to Player C; side pot (400) to Player C; Player A wins back their 100 from main pot if they beat Player B'
    ],
    correct: 'C',
    explanation: 'Main pot: 100×3=300 (all three eligible). Side pot 1: 200×2=400 (B and C only). Side pot 2: 400 (C only — no other player covered). Player C wins all three pots with best hand. Player A cannot win a pot they\'re not eligible for.'
  },

  {
    id: 'tda_028', track: 'professional', category: 'tda_rules', difficulty: 'hard',
    question: 'A player announces "raise" and puts out enough chips to call. Before adding more chips they are interrupted by a question from another player. What is their minimum obligation?',
    options: [
      'A) The call amount only — the raise is incomplete',
      'B) A minimum raise — the verbal declaration of raise is binding',
      'C) They may complete or withdraw the raise since chips are not fully forward',
      'D) The floor decides based on intent'
    ],
    correct: 'B',
    explanation: 'The verbal declaration of "raise" is binding. The player must complete at least the minimum raise. The interruption is irrelevant — verbal action locks them into raising. Only the amount can be adjusted if chips haven\'t been fully placed.'
  },

  {
    id: 'tda_029', track: 'professional', category: 'tda_rules', difficulty: 'hard',
    question: 'The flop is dealt with only two cards instead of three. The error is noticed immediately after betting begins. What is the correct procedure?',
    options: [
      'A) Deal the third flop card immediately without a burn',
      'B) Halt action, burn one card, deal the missing flop card',
      'C) Halt action, shuffle the remaining deck, burn and deal the third card',
      'D) Complete betting on the two-card flop, then deal the third card as a separate street'
    ],
    correct: 'A',
    explanation: 'If a flop is dealt short (two cards instead of three), the deck is not reshuffled. The third card is dealt without an additional burn — the original burn card covered this deal. Action that began is void and restarts.'
  },

  {
    id: 'tda_030', track: 'professional', category: 'tda_rules', difficulty: 'hard',
    question: 'Player A bets. Player B raises all-in. The raise is not a full raise (it is 40% of the minimum). Player A had already bet. Can Player A re-raise?',
    options: [
      'A) Yes — any all-in reopens action to the original bettor',
      'B) No — a sub-minimum all-in does not reopen action; Player A may only call or fold',
      'C) Yes — Player A is the original aggressor and always retains the right to re-raise',
      'D) Only if Player A verbally declared intent to raise before Player B acted'
    ],
    correct: 'B',
    explanation: 'A sub-minimum all-in (less than a full raise) does not reopen action to players who have already acted. Player A may call the additional amount or fold but may not raise. Only a full raise reopens action.'
  },

  {
    id: 'tda_031', track: 'professional', category: 'tda_rules', difficulty: 'hard',
    question: 'You accidentally deals the turn card without burning. The error is discovered after one player bets. What is the correct procedure?',
    options: [
      'A) The turn stands — missing a burn is not sufficient grounds to change the card',
      'B) The turn card is put back, a burn card is removed, and the same turn card is redealt',
      'C) The turn card is shuffled back into the deck and a new turn is properly dealt with a burn',
      'D) The floor is called and the decision is at their discretion'
    ],
    correct: 'A',
    explanation: 'Missing a burn card does not change the board card. The card dealt is the turn. Play continues normally. Burn cards exist to protect against cheating/marked cards — missing one does not invalidate the deal.'
  },

  {
    id: 'tda_032', track: 'professional', category: 'tda_rules', difficulty: 'hard',
    question: 'A player inadvertently shows one of their hole cards while folding to a bet. Another player demands to see the card. What is the correct ruling?',
    options: [
      'A) The folded card is shown to all players since it was exposed',
      'B) Only the player who saw it accidentally is entitled to see it',
      'C) No one is entitled to see a folded hand — the card goes into the muck',
      'D) The betting player who caused the fold is entitled to see the card'
    ],
    correct: 'A',
    explanation: 'Any card inadvertently exposed during a fold is shown to all active players — one player cannot have an informational advantage over others. The dealer shows the card to the table before it enters the muck.'
  },

  {
    id: 'tda_033', track: 'professional', category: 'tda_rules', difficulty: 'hard',
    question: 'In a tournament, a player at another table is eliminated and the tables need to balance. A player is moved to the new table and must post. What is their posting obligation?',
    options: [
      'A) They always post a big blind immediately',
      'B) They may wait for the big blind to reach their new seat with no post required',
      'C) They post a big blind unless they are moving into the small blind or button position, in which case they wait',
      'D) They post a big blind in any position except the big blind itself'
    ],
    correct: 'D',
    explanation: 'A player moved for table balancing must post a big blind in any position except the big blind position itself (where posting would be redundant). They cannot wait — this prevents players from avoiding blinds by requesting moves.'
  },

  {
    id: 'tda_034', track: 'professional', category: 'tda_rules', difficulty: 'hard',
    question: 'At showdown, Player A tables a hand and Player B mucks without showing. Player A\'s tabled hand does not beat the board. Who wins?',
    options: [
      'A) Player B wins since Player A\'s hand cannot beat the board',
      'B) Player A wins — tabled cards win over mucked cards regardless',
      'C) The pot is pushed to Player A since Player B conceded by mucking',
      'D) The floor is called to determine if the muck was intentional'
    ],
    correct: 'C',
    explanation: 'When a player mucks their hand at showdown, they surrender the pot. Player A wins regardless of hand strength because cards speak only when tabled. Player B\'s muck is a concession of the pot.'
  },

  {
    id: 'tda_035', track: 'professional', category: 'tda_rules', difficulty: 'hard',
    question: 'The board pairs on the river creating a possible full house for several players. You award the pot to the wrong player by misreading a hand. The error is discovered after the next hand has begun. What happens?',
    options: [
      'A) The pot cannot be corrected once the next hand has begun',
      'B) Play stops, the pot is corrected if the error can be proven beyond doubt',
      'C) The floor reviews surveillance footage to determine the correct winner',
      'D) The error is corrected regardless of how many hands have passed'
    ],
    correct: 'B',
    explanation: 'Significant pot errors can be corrected even after the next hand begins, but become increasingly difficult to correct as time passes. The TD makes the ruling — if the error can be established with certainty, the pot is corrected. Prevention through careful reading is the dealer\'s responsibility.'
  },

  {
    id: 'tda_036', track: 'professional', category: 'tda_rules', difficulty: 'hard',
    question: 'A player makes a bet that is all-in. Another player calls. A third player wants to raise. The all-in bet was a full raise. Can the third player raise, and does it reopen action to the caller?',
    options: [
      'A) The third player can raise; it does not reopen action to the caller',
      'B) The third player can raise; it reopens action to the caller',
      'C) The third player cannot raise — all-in bets do not permit re-raises',
      'D) Only the caller can raise since they acted after the all-in'
    ],
    correct: 'B',
    explanation: 'A full all-in raise does reopen action. The third player may raise. Since this is a new raise (a full one), action is reopened to all players including those who previously called — they may now call the raise, re-raise, or fold.'
  },

  {
    id: 'tda_037', track: 'professional', category: 'tda_rules', difficulty: 'hard',
    question: 'A player has 1,000 chips and makes what they believe is a 500 chip raise. The dealer counts down and finds only 475 chips were moved forward. The previous bet was 200. What is the ruling?',
    options: [
      'A) It is a call of 200 — insufficient for a raise',
      'B) It is a raise to 475 — whatever was moved forward',
      'C) It depends on whether 475 is at least 50% of the minimum raise (400) — if so, it is completed to a full 400 raise',
      'D) The player must either complete to 500 or reduce to a call'
    ],
    correct: 'C',
    explanation: 'Under the 50% rule: the minimum raise would be 400 total (raising by 200). 475 is more than 50% of the minimum raise increment, so it is treated as a raise and completed to the minimum of 400. The player owes nothing extra — 475 exceeds the minimum raise amount.'
  },

  {
    id: 'tda_038', track: 'professional', category: 'tda_rules', difficulty: 'hard',
    question: 'Two players remain. Player A is the small blind/button. Player B is the big blind. Heads up, who acts first preflop and who acts first postflop?',
    options: [
      'A) Player A acts first both preflop and postflop',
      'B) Player B acts first preflop; Player A acts first postflop',
      'C) Player A acts first preflop; Player B acts first postflop',
      'D) Player B acts first both preflop and postflop'
    ],
    correct: 'C',
    explanation: 'Heads up: the button is the small blind and acts first preflop (the non-button big blind acts last preflop). Postflop, normal position applies — the non-button (big blind, Player B) acts first. The button has position postflop.'
  },

  {
    id: 'tda_039', track: 'professional', category: 'tda_rules', difficulty: 'hard',
    question: 'A player is penalized with a one-round away-from-table penalty. Their blind comes up during the penalty. What happens?',
    options: [
      'A) The blind is missed and posted as a dead blind when the player returns',
      'B) The blind is skipped entirely — the player pays no blind for that round',
      'C) The penalty is paused while the player posts their blind, then resumes',
      'D) The hand is dealt to the player\'s empty seat and folded, blinds are posted normally'
    ],
    correct: 'A',
    explanation: 'During a penalty, blinds are missed. When the player returns, missed blinds are posted as dead blinds (they go into the pot but the player does not receive the option to raise their own blind). The penalty does not exempt a player from blind obligations.'
  },

  {
    id: 'tda_040', track: 'professional', category: 'tda_rules', difficulty: 'hard',
    question: 'A player tables the nuts on the river and you begin pushing the pot. Another player at the table says "wait — that hand is not the best hand" and points out a better holding. The dealer already touched the pot. What is the correct action?',
    options: [
      'A) The pot push stands — once the dealer touches the pot the hand is over',
      'B) Stop the push, verify the claim, correct the award if accurate — cards speak',
      'C) Only the floor can reverse a pot push in progress',
      'D) The player who objected must have been in the hand to have standing to object'
    ],
    correct: 'B',
    explanation: 'Cards speak — the dealer must stop and verify immediately. Any player at the table may point out an error in hand reading. The dealer touching the pot does not make an incorrect award final. Correct the pot if the better hand is confirmed.'
  },

];

export default TDA_RULES_QUESTIONS;
